// ─── Master classes service ─────────────────────────────────────────────────
//
// LECTURAS: directas contra Supabase con la anon key (RLS deja ver activos /
// config públicamente). Ver src/services/supabase/client.js.
//
// ESCRITURAS (panel admin): pasan por la Edge Function `bright-responder`, que
// valida que seas admin (vía /decode de Hutrit) y escribe con la service-role
// key (ignora RLS). Así no dependemos de firmar JWTs. Ver docs/supabase/webinar-admin.ts.
//
// El SELECT de lectura usa alias de PostgREST (`camelCase:snake_case`) para
// devolver el shape que ya consumían las páginas. En las escrituras, el mapeo a
// columnas lo hace la function; el front solo manda el payload en camelCase.
//
// Modelo Master class:
//   { id, title, topic, description, learnings: [{ icon, text }], date ("YYYY-MM-DD"),
//     time ("HH:mm"), timezone, durationMinutes, modality, registrationUrl, active,
//     bannerImageUrl, courseId, createdAt, updatedAt }
//
// courseId: id (numérico) del curso de la Academia que la desbloquea. Si es
// null/undefined, la master class no depende de ningún curso (siempre abierta).

import { supabase } from '../supabase/client'

// ─── Catálogo de iconos disponibles para learnings ──────────────────────────
export const LEARNING_ICONS = [
  'terminal',
  'sparkles',
  'api',
  'user',
  'camera',
  'briefcase',
  'calendar',
  'clock',
]

export const MODALITIES = ['Online en vivo', 'Presencial', 'Híbrido', 'Grabado']

// ─── Proyección de lectura (snake_case BD -> camelCase contrato front) ───────
const MASTER_CLASS_COLS =
  'id, title, topic, description, learnings, date, time, timezone, ' +
  'durationMinutes:duration_minutes, modality, registrationUrl:registration_url, ' +
  'active, bannerImageUrl:banner_image_url, courseId:course_id, ' +
  'createdAt:created_at, updatedAt:updated_at'

const CONFIG_COLS =
  'heroTitle:hero_title, heroSubtitle:hero_subtitle, ' +
  'learningsSectionTitle:learnings_section_title, ' +
  'scheduleSectionTitle:schedule_section_title, ' +
  'scheduleSubtitle:schedule_subtitle, warningCopy:warning_copy'

// ─── Llamada a la function admin ──────────────────────────────────────────────
const ADMIN_FN_NAME = 'bright-responder'

const getHutritToken = () => {
  try {
    return JSON.parse(localStorage.getItem('token'))?.token
  } catch (error) {
    console.log(error)
    return undefined
  }
}

const callAdmin = async (action, args = {}) => {
  const { data, error } = await supabase.functions.invoke(ADMIN_FN_NAME, {
    body: { token: getHutritToken(), action, ...args },
  })
  if (error) throw error
  return data
}

// ─── Lectura pública (talentos): RLS deja ver solo activos ───────────────────

export const getActiveMasterClasses = async () => {
  try {
    const { data, error } = await supabase
      .from('webinars')
      .select(MASTER_CLASS_COLS)
      .eq('active', true)
      .order('date', { ascending: true })
      .order('time', { ascending: true })
    if (error) throw error
    return data || []
  } catch (error) {
    console.log(error)
    return []
  }
}

export const getConfig = async () => {
  try {
    const { data, error } = await supabase
      .from('webinar_config')
      .select(CONFIG_COLS)
      .eq('id', 1)
      .maybeSingle()
    if (error) throw error
    return data || {}
  } catch (error) {
    console.log(error)
    return {}
  }
}

// ─── CRUD admin: vía Edge Function (service-role + validación de admin) ───────

export const getMasterClasses = async () => {
  try {
    // GET directo (anon). Ve activos e inactivos: la RLS de SELECT es pública.
    const { data, error } = await supabase
      .from('webinars')
      .select(MASTER_CLASS_COLS)
      .order('created_at', { ascending: false })
    if (error) throw error
    return data || []
  } catch (error) {
    console.log(error)
    return []
  }
}

export const createMasterClass = (data = {}) =>
  callAdmin('create', { payload: data })

export const updateMasterClass = (id, data = {}) =>
  callAdmin('update', { id, payload: data })

export const deleteMasterClass = (id) => callAdmin('delete', { id })

export const saveConfig = (data = {}) =>
  callAdmin('saveConfig', { payload: data })
